/**
 * Nova e Editar atividade.
 *
 * Uma tela só, dois modos: o design especifica a edição como a criação
 * pré-preenchida, mais os campos de local, notas e status.
 *
 * **Editar o horário é explícito e só acontece aqui.** Nenhuma operação de
 * reordenação toca `startTime` — a regra vive em `@nv/core` e é o motivo de
 * este campo existir separado.
 */
import { type Activity, type ActivityStatus, ALERT_MINUTES_OPTIONS, NOTAS_MAX, fimValido } from '@nv/core';
import { type FormEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useTrip } from '../../trip/TripContext';
import { Card, ConfirmSheet, ScreenHeader, Skeleton } from '../../ui/primitives';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { useCarregarDia } from './carregarDia';
import { LocalField, type LugarDaAtividade } from './LocalField';
import { useItinerary } from './store';

const STATUS: ActivityStatus[] = ['planejada', 'agora', 'concluida', 'cancelada'];
const STATUS_LABEL: Record<ActivityStatus, string> = {
  planejada: 'Planejada',
  agora: 'Agora',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

export function AtividadeFormScreen({ mode }: { mode: 'nova' | 'editar' }) {
  const { dia = '', atividade = '' } = useParams();
  // O formulário carrega o dia por conta própria. Quem chega aqui pela Home
  // ("Nova atividade") ou por link direto não passou pela página do dia — e o
  // store, sem o dia, descartava a mutação em silêncio: a atividade aparecia
  // na tela e nunca chegava ao servidor.
  const doDia = useCarregarDia(dia);
  const pronto = useItinerary((s) => s.ready && s.dayId !== '');
  const existente = useItinerary((s) => s.activities.find((a) => a.id === atividade));
  const carregado = useItinerary((s) => s.activities.length > 0);

  if (!pronto || doDia.carregando) {
    return (
      <section className="screen">
        <Skeleton height={64} count={4} />
      </section>
    );
  }

  // Abrir /editar por deep link chega antes da carga dos dados. Sem esta
  // espera, o formulário nasceria vazio e "Salvar" ficaria desabilitado para
  // sempre — os `useState` abaixo só inicializam uma vez.
  if (mode === 'editar' && !existente) {
    return (
      <section className="screen">
        {carregado ? (
          <p className="dia-footer">Atividade não encontrada.</p>
        ) : (
          <Skeleton height={64} count={4} />
        )}
      </section>
    );
  }

  // A chave garante estado novo ao trocar de atividade sem desmontar a rota.
  return <Formulario key={existente?.id ?? 'nova'} mode={mode} dia={dia} existente={existente} />;
}

function Formulario({
  mode,
  dia,
  existente,
}: {
  mode: 'nova' | 'editar';
  dia: string;
  existente: Activity | undefined;
}) {
  const navigate = useNavigate();
  const createActivity = useItinerary((s) => s.createActivity);
  const updateActivity = useItinerary((s) => s.updateActivity);
  const deleteActivity = useItinerary((s) => s.deleteActivity);
  const maiorOrdem = useItinerary((s) =>
    s.activities.reduce((max, a) => Math.max(max, a.sortOrder), 0),
  );

  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [title, setTitle] = useState(existente?.title ?? '');
  const [startTime, setStartTime] = useState(existente?.startTime ?? '');
  // Fim e "dia inteiro" (PO, 22/09/2026): o fim é opcional; dia inteiro
  // dispensa os dois campos (a atividade não tem hora).
  const [endTime, setEndTime] = useState(existente?.endTime ?? '');
  const [allDay, setAllDay] = useState(existente?.allDay ?? false);
  const [locationName, setLocationName] = useState(existente?.locationName ?? '');
  const [lugar, setLugar] = useState<LugarDaAtividade | null>(
    existente && existente.placeId && typeof existente.latitude === 'number' && typeof existente.longitude === 'number'
      ? {
          placeId: existente.placeId,
          placeAddress: existente.placeAddress ?? null,
          latitude: existente.latitude,
          longitude: existente.longitude,
        }
      : null,
  );
  const [notes, setNotes] = useState(existente?.notes ?? '');
  const [status, setStatus] = useState<ActivityStatus>(existente?.status ?? 'planejada');
  const [alerta, setAlerta] = useState<number | null>(existente?.alertMinutesBefore ?? null);
  // Quem vai: vazio = todo mundo (o padrão). É o que os carimbos leem.
  const { members } = useTrip();
  const [quemVai, setQuemVai] = useState<string[]>(existente?.participantMemberIds ?? []);
  const alternar = (memberId: string) => setQuemVai((lista) => (lista.includes(memberId) ? lista.filter((m) => m !== memberId) : [...lista, memberId]));
  const [tocouTitulo, setTocouTitulo] = useState(false);

  const tituloVazio = title.trim().length === 0;
  const horaInvalida = !allDay && startTime.length > 0 && !/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime);
  const fimInvalido = !allDay && endTime.length > 0 && !fimValido(startTime || '00:00', endTime);
  const podeSalvar = !tituloVazio && !horaInvalida;

  const [salvando, setSalvando] = useState(false);

  const salvar = async (event: FormEvent) => {
    event.preventDefault();
    if (!podeSalvar || salvando) {
      setTocouTitulo(true);
      return;
    }
    setSalvando(true);

    if (mode === 'editar' && existente) {
      await updateActivity(existente.id, {
        title: title.trim(),
        // Só entra no patch se realmente mudou — assim o histórico registra
        // "alterou horário" apenas quando é verdade.
        ...(startTime !== existente.startTime ? { startTime } : {}),
        ...((existente.endTime ?? '') !== endTime ? { endTime } : {}),
        ...((existente.allDay ?? false) !== allDay ? { allDay } : {}),
        locationName: locationName.trim() || undefined,
        // Lugar estruturado vai sempre, inclusive nulo: remover o lugar do
        // mapa também é uma edição.
        placeId: lugar?.placeId ?? null,
        placeAddress: lugar?.placeAddress ?? null,
        latitude: lugar?.latitude ?? null,
        longitude: lugar?.longitude ?? null,
        notes: notes.trim() || undefined,
        alertMinutesBefore: alerta,
        status,
        participantMemberIds: quemVai,
      });
    } else {
      const nova: Activity = {
        id: globalThis.crypto?.randomUUID?.() ?? `a-${Date.now()}`,
        title: title.trim(),
        startTime: allDay ? '00:00' : startTime || '12:00',
        ...(allDay ? { allDay: true } : endTime ? { endTime } : {}),
        locationName: locationName.trim() || undefined,
        ...(lugar
          ? {
              placeId: lugar.placeId,
              placeAddress: lugar.placeAddress,
              latitude: lugar.latitude,
              longitude: lugar.longitude,
            }
          : {}),
        notes: notes.trim() || undefined,
        ...(alerta === null ? {} : { alertMinutesBefore: alerta }),
        status: 'planejada',
        sortOrder: maiorOrdem + 10,
        ...(quemVai.length > 0 ? { participantMemberIds: quemVai } : {}),
      };
      await createActivity(nova);
    }

    // Só depois de durável: ver o comentário em `updateActivity` do store.
    navigate(-1);
  };

  return (
    <section className="screen">
      <ScreenHeader title={mode === 'editar' ? 'Editar atividade' : 'Nova atividade'} />

      <form className="form" onSubmit={(e) => void salvar(e)} noValidate>
        <label className={tocouTitulo && tituloVazio ? 'field field--error' : 'field'}>
          <span className="field__label">
            Título <span className="field__required">*</span>
          </span>
          <input
            className="field__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setTocouTitulo(true)}
            maxLength={60}
            required
            aria-invalid={tocouTitulo && tituloVazio}
          />
          {tocouTitulo && tituloVazio && (
            <span className="field__error">Campo obrigatório</span>
          )}
        </label>

        <div className="form-row">
          <label className="field field--half">
            <span className="field__label">Dia</span>
            <input className="field__input" value={dia} readOnly />
          </label>

          {/* Horário: campo explícito. Reordenar nunca chega aqui. */}
          {!allDay && (
            <label className={horaInvalida ? 'field field--half field--error' : 'field field--half'}>
              <span className="field__label">Começa</span>
              <input
                className="field__input"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                aria-invalid={horaInvalida}
              />
              {horaInvalida && <span className="field__error">Use o formato 24h, ex.: 14:30</span>}
            </label>
          )}
        </div>

        {/* Término e "dia inteiro" (PO, 22/09/2026). O fim é opcional: sem ele,
            a atividade continua sendo só "começa às". */}
        <div className="form-row">
          {!allDay && (
            <label className={fimInvalido ? 'field field--half field--error' : 'field field--half'}>
              <span className="field__label">Termina (opcional)</span>
              <input
                className="field__input"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                aria-invalid={fimInvalido}
                data-testid="ativ-fim"
              />
              {fimInvalido && <span className="field__error">O término precisa ser depois do início.</span>}
            </label>
          )}
          <div className="field field--half ativ__dia-inteiro">
            <span className="field__label">Dia inteiro</span>
            <button
              type="button"
              role="switch"
              aria-checked={allDay}
              aria-label="Dia inteiro"
              className={`mapa__toggle${allDay ? ' mapa__toggle--on' : ''}`}
              onClick={() => setAllDay((v) => !v)}
              data-testid="ativ-dia-inteiro"
            >
              <span className="mapa__toggle-bola" />
            </button>
          </div>
        </div>

        {/* Local e notas já na criação — quem cria sabe onde e o quê.
            Status fica só na edição: atividade nova nasce planejada. */}
        <LocalField
          nome={locationName}
          lugar={lugar}
          onChange={(novoNome, novoLugar) => {
            setLocationName(novoNome);
            setLugar(novoLugar);
          }}
        />

        {/* Aviso é da atividade: quem organiza decide o que merece push. Cada
            pessoa liga/desliga o recebimento no próprio perfil. */}
        <fieldset className="field">
          <legend className="field__label">Avisar antes</legend>
          <div className="chip-row" role="radiogroup" aria-label="Avisar antes">
            {([null, ...ALERT_MINUTES_OPTIONS] as (number | null)[]).map((min) => (
              <button
                type="button"
                key={min ?? 'nao'}
                role="radio"
                aria-checked={alerta === min}
                className={alerta === min ? 'pick pick--on' : 'pick'}
                onClick={() => setAlerta(min)}
              >
                {min === null ? 'Não avisar' : min >= 60 ? `${min / 60} h` : `${min} min`}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Quem vai: vazio é "todo mundo" — e é o que vira carimbo no passaporte
            de cada pessoa. Um toque no avatar marca; outro desmarca. */}
        <fieldset className="field">
          <legend className="field__label">Quem vai? {quemVai.length === 0 ? '· TODO MUNDO' : `· ${quemVai.length} DE ${members.length}`}</legend>
          <div className="chip-row" role="group" aria-label="Quem vai">
            <button type="button" className={quemVai.length === 0 ? 'pick pick--on' : 'pick'} onClick={() => setQuemVai([])} data-testid="quem-todos">
              Todo mundo
            </button>
            {members.map((m) => (
              <button
                type="button"
                key={m.memberId}
                role="checkbox"
                aria-checked={quemVai.includes(m.memberId)}
                className={quemVai.includes(m.memberId) ? 'pick pick--on quem__pick' : 'pick quem__pick'}
                onClick={() => alternar(m.memberId)}
                data-testid="quem-membro"
              >
                <FotoAvatar nome={m.name} url={m.avatarUrl} cor={m.familyColor} tamanho={22} className="quem__avatar" />
                {m.name.split(/\s+/)[0]}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="field">
          <span className="field__label">Notas</span>
          <textarea
            className="field__input field__textarea"
            rows={4}
            maxLength={NOTAS_MAX}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {/* O teto fica visível: um tester bateu nos 500 sem saber (20/09). */}
          <span className="field__contador" aria-live="polite" data-testid="notas-contador">
            {notes.length}/{NOTAS_MAX}
          </span>
        </label>

        {mode === 'editar' && (
          <>
            <fieldset className="field">
              <legend className="field__label">Status</legend>
              <div className="chip-row">
                {STATUS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={status === s ? 'pick pick--on' : 'pick'}
                    aria-pressed={status === s}
                    onClick={() => setStatus(s)}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            </fieldset>
          </>
        )}


        <button type="submit" className="nv-btn nv-btn--primary nv-btn--block" disabled={!podeSalvar || salvando}>
          {salvando ? 'Salvando…' : mode === 'editar' ? 'Salvar alterações' : 'Adicionar ao roteiro'}
        </button>

        {mode === 'editar' && existente && (
          <button type="button" className="nv-btn nv-btn--danger nv-btn--block" onClick={() => setConfirmandoExclusao(true)} data-testid="excluir-atividade">
            Excluir atividade
          </button>
        )}
      </form>

      {confirmandoExclusao && existente && (
        <ConfirmSheet
          titulo="Excluir esta atividade?"
          texto={`"${existente.title}" sai do roteiro de todo mundo. O histórico do dia registra a exclusão.`}
          confirmar="Excluir atividade"
          testId="confirmar-exclusao"
          onCancel={() => setConfirmandoExclusao(false)}
          onConfirm={() => {
            // Espera a exclusão ficar durável (fila + estado) antes de sair:
            // navegar antes recarregaria o dia com a atividade ainda lá.
            void deleteActivity(existente.id).then(() => navigate(`/roteiro/${dia}`));
          }}
        />
      )}

      <Card variant="chip">
        <p className="home-prep__note">
          Reordenar o dia muda a ordem, nunca o horário. Para mudar o horário,
          é aqui.
        </p>
      </Card>
    </section>
  );
}
